import './Cart.css';
import { UiContainer } from '../../state/ui';
import { useCart } from '../../state/cart';
import { useTranslation } from '../../i18n/i18n';
import { Loader } from '../Loader/Loader';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { CartItemListing } from './CartItemListing';
import { CartFooter } from './CartFooter';
import { CartTotalsTable } from './CartTotalsTable';
import { Overlay } from '../Overlay/Overlay';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useConfig } from '../../config';
import { useEffect, type MouseEvent } from 'react';
import { UpdateErrorsModal } from './UpdateErrorsModal/UpdateErrorsModal';
import { cookieService } from '../cookieService';

export const Cart = (): JSX.Element => {
	const ui = UiContainer.useContainer();

	return (
		<>
			{ui.isCartOpen ? <Overlay toggle={ui.toggleCart} className="cart-overlay" onClick={ui.toggleCart} /> : null}
			<div id="cart" className={ui.isCartOpen ? 'open' : ''}>
				<CartContent />
			</div>
			<UpdateErrorsModal />
		</>
	);
};

export const CartContent = (): JSX.Element => {
	const { cart, error, setQuantity } = useCart();
	const t = useTranslation().cart;
	const ui = UiContainer.useContainer();
	const { sendEvent } = useAnalytics();
	const config = useConfig();
	const hasSessionMigrationCookie = Boolean(cookieService.read('session_migration'));
	const handleMigrationClick = (event: MouseEvent<HTMLAnchorElement>) => {
		const returnPath = encodeURIComponent(new URL(window.location.href).pathname);
		event.currentTarget.href = `https://api.worky.dk/migrate?return=${returnPath}&apology=/gammel-indkobskurv-mangler`;
	};

	useEffect(() => {
		if (ui.isCartOpen && cart) {
			sendEvent({
				event: 'view_cart',
				ecommerce: {
					currency: config.currencyCode,
					value: cart.totals?.total || 0,
					items: cart.items.map((item) => ({
						item_id: String(item.product.id),
						item_name: item.product.name,
						price: item.price.currentPrice,
					})),
				},
			});
		}
	}, [ui.isCartOpen]);

	if (error) return <ErrorMessage />;
	if (!cart) return <Loader />;

	return (
		<>
			<div id="cart-wrapper">
				<div id="cart-content-wrapper">
					<div id="cart-top">
						<p className="cart-heading non-seo-h1">{t.cart}</p>
						<div className="not-mobile">
							<cart-item-heading>
								<span>{t.product}</span>
								<span className="text-center">{t.amount}</span>
								<span className="text-right">{t.total}</span>
							</cart-item-heading>
						</div>
						<cart-items>
							{(config.domain === 'ondio.dk' || config.domain === 'stage.www.ondio.dk') &&
							cart.items.length === 0 &&
							!hasSessionMigrationCookie ? (
								<p>
									Savner du din indkøbskurv?{' '}
									<a
										href="https://api.worky.dk/migrate?return=%2F&apology=/gammel-indkobskurv-mangler"
										onClick={handleMigrationClick}
									>
										Klik her
									</a>{' '}
									for at hente den fra Worky.
								</p>
							) : (
								<CartItemListing items={cart.items} onSetQuantity={setQuantity} />
							)}
						</cart-items>
					</div>
					<CartTotalsTable cart={cart} />
				</div>
			</div>
			<CartFooter />
		</>
	);
};
